import type { MerchKind } from '@/components/shop/MerchIcon';
import i18n from '@/i18n';
import { formatCurrency } from '@/lib/format';
import type { ApiProduct } from '@/api/shop';
import { resolveMediaUrl } from '@/lib/config';

// Presentation adapter for the merch catalog.
//
// This file used to BE the catalog: five products hardcoded in the app and
// mirrored in Go, which meant adding a scarf needed two code changes and an app
// release. Track 06 (MRC-01) moved the catalog into the database; what is left
// here is the translation from an API row to what the shop screens want to
// draw, plus the i18n lookups.
//
// The screens still need an icon `kind`, a subtitle, colour swatches and a size
// run — none of which are catalog facts a merch admin should have to express in
// code. They ride along in the product's `metadata` jsonb, so the club can edit
// them without a build.

/** The league key the store is scoped to. One club, one league for now. */
export const SHOP_LEAGUE = 'basketball';

export type ColorOption = { key: string; label: string; swatch: string };

/** The shape the shop screens render. */
export type ShopProduct = {
  id: string;
  /** Stable slug — the i18n key, and what the retired catalog used as its id. */
  sku: string;
  name: string;
  priceCents: number;
  /** Sale-aware; equals priceCents when nothing is on promotion. */
  effectivePriceCents: number;
  onSale: boolean;
  inStock: boolean;
  isPersonalised: boolean;
  subtitle: string;
  kind: MerchKind;
  categoryId: string;
  colors: ColorOption[];
  sizes: string[];
  featured: boolean;
  /** The product's photograph, absolute and ready for <Image>. Undefined when
   *  the product has no image, which is when the drawn stand-in is used. */
  imageUrl?: string;
};

const FALLBACK_KIND: MerchKind = 'jersey';
const ONE_SIZE = 'One size';

/** Icon kinds MerchIcon knows. Anything else falls back rather than rendering
 *  nothing, so a newly added product type is never invisible. */
const KNOWN_KINDS: MerchKind[] = ['jersey', 'cap', 'scarf', 'hoodie', 'ball'];

function kindOf(meta: ApiProduct['metadata']): MerchKind {
  const k = meta?.kind as MerchKind | undefined;
  return k && KNOWN_KINDS.includes(k) ? k : FALLBACK_KIND;
}

/** toShopProduct maps one API row onto what the screens draw. */
export function toShopProduct(p: ApiProduct): ShopProduct {
  const meta = p.metadata ?? {};
  return {
    id: p.id,
    sku: p.sku,
    name: p.name,
    priceCents: p.price_cents,
    effectivePriceCents: p.effective_price_cents || p.price_cents,
    onSale: !!p.on_sale,
    inStock: p.in_stock,
    isPersonalised: !!p.is_personalised,
    subtitle: meta.subtitle ?? p.description ?? '',
    kind: kindOf(meta),
    categoryId: p.category_id,
    colors: meta.colors ?? [],
    sizes: meta.sizes?.length ? meta.sizes : [ONE_SIZE],
    featured: !!meta.featured,
    // image_blob_id is an id, not a URL: the bytes come from the API's blob
    // route, so it is resolved here once rather than in each screen that draws
    // a product.
    imageUrl: p.image_blob_id ? resolveMediaUrl(`/blobs/${p.image_blob_id}`) : undefined,
  };
}

export const toShopProducts = (products: ApiProduct[]) => products.map(toShopProduct);

/** The hero card's product: whatever the merch team flagged, else the first. */
export function featuredOf(products: ShopProduct[]): ShopProduct | undefined {
  return products.find((p) => p.featured) ?? products[0];
}

export const hasRealSizes = (p: ShopProduct) => !(p.sizes.length === 1 && p.sizes[0] === ONE_SIZE);

export const formatPrice = (cents: number) => formatCurrency(cents);

// Display copy is translated through the shop namespace, keyed by SKU — the
// stable slug that survived the move to uuid ids, so the existing translations
// keep resolving. The server's values are the defaultValue fallbacks, which is
// what a product added by an admin (with no translation yet) will show.
export const productName = (p: ShopProduct) =>
  i18n.t(`shop:products.${p.sku}.name`, { defaultValue: p.name });
// No server subtitle, nothing to translate (and no key to go missing).
export const productSubtitle = (p: ShopProduct) =>
  p.subtitle ? i18n.t(`shop:products.${p.sku}.subtitle`, { defaultValue: p.subtitle }) : '';
export const categoryLabel = (name: string) =>
  i18n.t(`shop:categories.${name.toLowerCase()}`, { defaultValue: name });
export const colorLabel = (c: ColorOption) =>
  i18n.t(`shop:colors.${c.key}`, { defaultValue: c.label });
export const sizeLabel = (s: string) =>
  s === ONE_SIZE ? i18n.t('shop:oneSize', { defaultValue: s }) : s;
